import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import Amt from '@/common/components/amt';
import SummaryItem from '@/common/components/summary-item';
import TutorialSpotlight from '@/common/components/tutorial-spotlight';
import { useBootstrap } from '@/auth/bootstrap-context';
import { useCloudSyncPaused } from '@/hooks/use-cloud-sync-paused';
import ModalDeleteForm from '@/modules/budget/modal-delete-form';
import BudgetSettingsModal from '@/modules/budget/modal-edit-form';
import DayPanel from '@/modules/calendar/day-panel';
import MonthGrid from '@/modules/calendar/month-grid';
import monthViewStyle from '@/modules/calendar/month-grid/style.module.scss';
import YearView from '@/modules/calendar/year-view';
import BottomBar from '@/modules/navigation/bottom-bar';
import TopBar from '@/modules/navigation/top-bar';
import AddTxModal from '@/modules/transactions/forms/modal-add-tx';
import EditSeriesModal from '@/modules/transactions/forms/modal-edit-series';
import EditTxModal from '@/modules/transactions/forms/modal-edit-tx';
import { useAppState } from '@/store/context';
import { computeSummary, computeBalances, monthEndStr, yearEndStr, todayStr, toStr } from '@/utils';
import style from './style.module.scss';
const BUDGET_STEPS = [
    {
        title: 'Сводка бюджета',
        text: 'Фактический баланс, прогноз на конец периода и свободный остаток. Меняется при переключении месяц/год.',
        selector: '[data-tut="summary-bar"]',
        place: 'bottom',
    },
    {
        title: 'Сетка календаря',
        text: 'Каждая ячейка — день. Чипы показывают операции: зелёные — доход, красные — расход. Одинарный тап по дню откроет панель с деталями, двойной — сразу форму добавления операции.',
        selector: '[data-tut="calendar-grid"]',
        place: 'left',
    },
    {
        title: 'Месяц / Год',
        text: 'Переключайтесь между детальным месячным видом и обзорным годовым.',
        selector: '[data-tut="view-toggle"]',
        place: 'bottom',
    },
    {
        title: 'Навигация по периодам',
        text: 'Стрелки листают месяцы или годы. Текущий период отображается по центру.',
        selector: '[data-tut="month-nav"]',
        place: 'bottom',
    },
    {
        title: 'Настройки бюджета',
        text: 'Здесь можно изменить название, иконку, категории и включить накопления.',
        selector: '[data-tut="settings-btn"]',
        place: 'bottom',
    },
];
function BudgetPage() {
    const { budgetId } = useParams();
    const { state, dispatch } = useAppState();
    const navigate = useNavigate();
    const { paused } = useCloudSyncPaused();
    const bootstrap = useBootstrap();
    // Импорт выписки — платная фича (грант statement_import_access). Кнопку
    // показываем только когда доступ есть; после оформления подписки bootstrap
    // перечитывается и кнопка появляется без ручного обновления.
    const canImportStatement = bootstrap.data?.storagePolicy?.statementImportEnabled === true;
    const budget = state.budgets.find((b) => b.id === budgetId);
    // Stage 3 read-only: a CloudBound budget is read-only while cloud sync is paused.
    const readOnly = paused && budget?.storageMode === 'CloudBound';
    useEffect(() => {
        if (budgetId && state.currentId !== budgetId) {
            dispatch({ type: 'SET_CURRENT_BUDGET', id: budgetId });
        }
    }, [budgetId, state.currentId, dispatch]);
    const [showAddTx, setShowAddTx] = useState(false);
    const [addTxDate, setAddTxDate] = useState(null);
    const [showSettings, setShowSettings] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [editSeriesTx, setEditSeriesTx] = useState(null);
    const [editTx, setEditTx] = useState(null);
    const { year, month, selectedDate, calMode } = state;
    const periodEnd = calMode === 'year' ? yearEndStr(year) : monthEndStr(year, month);
    const summary = useMemo(() => (budget ? computeSummary(budget, periodEnd) : { actual: 0, forecast: 0, free: 0 }), [budget, periodEnd]);
    useEffect(() => {
        if (calMode !== 'month')
            return;
        const onKey = (e) => {
            if (['INPUT', 'SELECT', 'TEXTAREA'].includes(e.target.tagName))
                return;
            if (e.key === 'Enter' && selectedDate && !readOnly) {
                setAddTxDate(selectedDate);
                setShowAddTx(true);
                return;
            }
            const dirs = {
                ArrowLeft: -1,
                ArrowRight: 1,
                ArrowUp: -7,
                ArrowDown: 7,
            };
            if (!dirs[e.key])
                return;
            e.preventDefault();
            const d = new Date((selectedDate || todayStr()) + 'T00:00:00');
            d.setDate(d.getDate() + dirs[e.key]);
            if (d.getFullYear() === year && d.getMonth() === month) {
                dispatch({ type: 'SELECT_DATE', date: toStr(d) });
            }
            else {
                dispatch({ type: 'GO_MONTH', year: d.getFullYear(), month: d.getMonth() });
                dispatch({ type: 'SELECT_DATE', date: toStr(d) });
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [calMode, selectedDate, year, month, dispatch, readOnly]);
    // Opening the add-transaction modal is a no-op on a read-only (paused CloudBound) budget.
    const openAddTx = (date) => {
        if (readOnly)
            return;
        setAddTxDate(date);
        setShowAddTx(true);
    };
    const navPrev = () => calMode === 'year'
        ? dispatch({ type: 'NAV_YEAR', delta: -1 })
        : dispatch({ type: 'NAV_MONTH', delta: -1 });
    const navNext = () => calMode === 'year'
        ? dispatch({ type: 'NAV_YEAR', delta: 1 })
        : dispatch({ type: 'NAV_MONTH', delta: 1 });
    const selTxs = useMemo(() => budget && selectedDate ? budget.transactions.filter((tx) => tx.date === selectedDate) : [], [budget?.transactions, selectedDate]);
    const balances = useMemo(() => {
        if (!budget || calMode !== 'month')
            return {};
        const cur = computeBalances(budget, year, month);
        const prevM = month === 0 ? 11 : month - 1;
        const prevY = month === 0 ? year - 1 : year;
        const nextM = month === 11 ? 0 : month + 1;
        const nextY = month === 11 ? year + 1 : year;
        return {
            ...computeBalances(budget, prevY, prevM),
            ...cur,
            ...computeBalances(budget, nextY, nextM),
        };
    }, [budget, year, month, calMode]);
    if (!budget)
        return _jsx(Navigate, { to: "/app", replace: true });
    const txOps = {
        onConfirm: (id, amt) => dispatch({ type: 'UPDATE_TX', id, updates: { actual: amt } }),
        onUnconfirm: (id) => dispatch({ type: 'UPDATE_TX', id, updates: { actual: null } }),
        onSetCat: (id, cat) => dispatch({ type: 'UPDATE_TX', id, updates: { category: cat } }),
        onDelete: (id) => dispatch({ type: 'DELETE_TX', id }),
        onEditSeries: (tx) => setEditSeriesTx(tx),
        onEditTx: (tx) => setEditTx(tx),
    };
    const dayPanel = selectedDate && calMode === 'month' && (_jsx(DayPanel, { date: selectedDate, transactions: selTxs, currency: budget.currency, budget: budget, balance: balances[selectedDate], onClose: () => dispatch({ type: 'SELECT_DATE', date: null }), onAddTx: () => openAddTx(selectedDate), ...txOps }));
    return (_jsxs("div", { className: style['budget-view'], children: [_jsx(TopBar, { icon: budget.icon || '💼', title: budget.name, year: year, month: month, calMode: calMode, budgetView: "calendar", onHome: () => navigate('/app'), onSettings: () => setShowSettings(true), onImport: canImportStatement ? () => navigate(`/bdgts/${budgetId}/import`) : undefined, onToggleView: () => navigate(`/bdgts/${budgetId}/cat`), onSetMode: (mode) => dispatch({ type: 'SET_MODE', mode }), onNavPrev: navPrev, onNavNext: navNext }), readOnly && (_jsx("div", { style: {
                    padding: '8px 14px',
                    background: 'var(--warning-bg, #fff4e5)',
                    color: 'var(--warning-fg, #8a5300)',
                    fontSize: 13,
                    textAlign: 'center',
                }, children: "\u0422\u043E\u043B\u044C\u043A\u043E \u0434\u043B\u044F \u0447\u0442\u0435\u043D\u0438\u044F \u2014 \u0441\u0438\u043D\u0445\u0440\u043E\u043D\u0438\u0437\u0430\u0446\u0438\u044F \u043F\u0440\u0438\u043E\u0441\u0442\u0430\u043D\u043E\u0432\u043B\u0435\u043D\u0430. \u041F\u0440\u043E\u0434\u043B\u0438\u0442\u0435 \u043F\u043E\u0434\u043F\u0438\u0441\u043A\u0443 \u0432 \u043F\u0440\u043E\u0444\u0438\u043B\u0435." })), _jsxs("div", { className: style['summary-bar'], "data-tut": "summary-bar", children: [_jsx(SummaryItem, { label: "\u0424\u0430\u043A\u0442\u0438\u0447\u0435\u0441\u043A\u0438\u0439 \u0431\u0430\u043B\u0430\u043D\u0441", shortLabel: "\u0411\u0430\u043B\u0430\u043D\u0441", info: "\u0424\u0430\u043A\u0442\u0438\u0447\u0435\u0441\u043A\u0438\u0439 \u0431\u0430\u043B\u0430\u043D\u0441 \u2014 \u0441\u0443\u043C\u043C\u0430 \u0432\u0441\u0435\u0445 \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0451\u043D\u043D\u044B\u0445 \u0434\u043E\u0445\u043E\u0434\u043E\u0432 \u0438 \u0440\u0430\u0441\u0445\u043E\u0434\u043E\u0432 \u043D\u0430 \u0442\u0435\u043A\u0443\u0449\u0443\u044E \u0434\u0430\u0442\u0443", value: _jsx(Amt, { value: summary.actual, currency: budget.currency }), neg: summary.actual < 0 }), _jsx(SummaryItem, { label: calMode === 'year' ? 'Прогноз на конец года' : 'Прогноз на конец месяца', shortLabel: "\u041F\u0440\u043E\u0433\u043D\u043E\u0437", info: calMode === 'year'
                            ? 'Прогноз на конец года — ожидаемый баланс с учётом запланированных операций'
                            : 'Прогноз на конец месяца — ожидаемый баланс с учётом запланированных операций', value: _jsx(Amt, { value: summary.forecast, currency: budget.currency }), neg: summary.forecast < 0 }), _jsx(SummaryItem, { label: "\u0421\u0432\u043E\u0431\u043E\u0434\u043D\u044B\u0439 \u043E\u0441\u0442\u0430\u0442\u043E\u043A", shortLabel: "\u041E\u0441\u0442\u0430\u0442\u043E\u043A", info: "\u0421\u0432\u043E\u0431\u043E\u0434\u043D\u044B\u0439 \u043E\u0441\u0442\u0430\u0442\u043E\u043A \u2014 \u0441\u0443\u043C\u043C\u0430, \u0434\u043E\u0441\u0442\u0443\u043F\u043D\u0430\u044F \u0434\u043B\u044F \u043D\u0435\u0437\u0430\u043F\u043B\u0430\u043D\u0438\u0440\u043E\u0432\u0430\u043D\u043D\u044B\u0445 \u0442\u0440\u0430\u0442", value: _jsx(Amt, { value: summary.free, currency: budget.currency }), neg: summary.free < 0, pos: summary.free > 0 })] }), calMode === 'month' ? (_jsxs("div", { className: monthViewStyle['calendar-container'], children: [_jsx(MonthGrid, { budget: budget, year: year, month: month, selectedDate: selectedDate, balances: balances, dispatch: dispatch, onAddTx: openAddTx }), selectedDate && dayPanel] })) : (_jsx(YearView, { budget: budget, year: year, dispatch: dispatch, onAddTx: openAddTx })), _jsx(BottomBar, { items: [
                    { key: 'home', icon: 'home', label: 'Бюджеты' },
                    { key: 'calendar', icon: 'calendar', label: 'Календарь' },
                    { key: 'dashboard', icon: 'dashboard', label: 'Категории' },
                ], activeKey: "calendar", onSelect: (key) => {
                    if (key === 'home')
                        navigate('/app');
                    if (key === 'dashboard')
                        navigate(`/bdgts/${budgetId}/cat`);
                } }), showAddTx && (_jsx(AddTxModal, { date: addTxDate || selectedDate || todayStr(), budget: budget, onClose: () => setShowAddTx(false), onAdd: (txs) => {
                    txs.forEach((tx) => dispatch({ type: 'ADD_TX', tx }));
                    setShowAddTx(false);
                } })), showSettings && (_jsx(BudgetSettingsModal, { budget: budget, onClose: () => setShowSettings(false), onSave: (updates) => {
                    dispatch({ type: 'UPDATE_BUDGET', id: budget.id, updates });
                    setShowSettings(false);
                }, onDelete: () => {
                    setShowSettings(false);
                    setShowDelete(true);
                } })), showDelete && (_jsx(ModalDeleteForm, { budgetName: budget.name, onClose: () => setShowDelete(false), onDelete: () => {
                    dispatch({ type: 'DELETE_BUDGET', id: budget.id });
                    navigate('/app');
                } })), editTx && !editTx.seriesId && (_jsx(EditTxModal, { tx: editTx, budget: budget, onClose: () => setEditTx(null), onSave: (id, updates) => {
                    dispatch({ type: 'UPDATE_TX', id, updates });
                    setEditTx(null);
                } })), editSeriesTx && editSeriesTx.seriesId && (_jsx(EditSeriesModal, { tx: editSeriesTx, budget: budget, allSeriesTxs: budget.transactions.filter((t) => t.seriesId === editSeriesTx.seriesId), onClose: () => setEditSeriesTx(null), onUpdateFrom: (seriesId, fromDate, updates) => dispatch({ type: 'UPDATE_SERIES_FROM', seriesId, fromDate, updates }), onDeleteFrom: (seriesId, fromDate) => dispatch({ type: 'DELETE_SERIES_FROM', seriesId, fromDate }), onExtend: (seriesId, txs) => dispatch({ type: 'EXTEND_SERIES', seriesId, txs }) })), _jsx(TutorialSpotlight, { steps: BUDGET_STEPS, storageKey: "tut_budget_cal_v1" })] }));
}
export default BudgetPage;
